import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  Download,
  Trash2,
  FileJson,
  FolderOpen,
  Loader2,
  RefreshCw,
  Info,
  AlertCircle,
} from "lucide-react";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Badge } from "@repo/ui/badge";
import { Label } from "@repo/ui/label";
import { Alert, AlertDescription } from "@repo/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/alert-dialog";
import { Checkbox } from "@repo/ui/checkbox";
import { ScrollArea } from "@repo/ui/scroll-area";
import { HelpTooltip } from "@/components/common/HelpTooltip";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { formatDate } from "@/lib/formatDate";
import {
  fetchExportRuntimeResolution,
  fetchExportRuntimeSettings,
  listSnapshotExports,
  runSnapshotProvenanceExport,
} from "@/lib/api";
import type { ExportRuntimeSettings, SnapshotExportFilters } from "@/lib/api";
import { ExportPreviewTable } from "@/components/exports/ExportPreviewTable";

interface StorageFile {
  id: string;
  name: string;
  adapter: string;
  created_at: string;
  size: number;
  metadata: {
    totalRecords?: number;
    config?: {
      suburbs?: string[];
      propertyType?: string;
    };
  } | null;
}

interface SnapshotExportRun {
  id: string;
  status: string;
  format: string;
  file_bucket: string | null;
  file_path: string | null;
  manifest_path: string | null;
  export_name: string | null;
  selected_columns: string[] | null;
  chunk_size_rows: number | null;
  chunk_count: number | null;
  metadata: Record<string, unknown> | null;
  file_size_bytes?: number | null;
  row_count: number;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
}

const statusBadgeClass: Record<string, string> = {
  completed: "bg-success/15 text-success",
  failed: "bg-destructive/15 text-destructive",
  running: "bg-primary/15 text-primary",
};

type ExportPreset = {
  id: string;
  label: string;
  description: string;
  columns: string[];
  defaultChunkSize?: number;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const KNOWN_EXPORT_COLUMNS = [
  "property_snapshot_id",
  "property_id",
  "full_address",
  "suburb",
  "state",
  "postcode",
  "snapshot_timestamp",
  "listing_type",
  "sale_date",
  "sale_price",
  "asking_price",
  "price_display",
  "bedrooms",
  "bathrooms",
  "parking",
  "property_type",
  "adapter_slug",
  "job_id",
  "job_run_id",
  "queue_item_id",
  "source_page_url",
  "page_snapshot_id",
  "page_snapshot_provider",
  "page_snapshot_bucket",
  "page_snapshot_path",
  "page_snapshot_content_encoding",
  "page_snapshot_captured_at",
  "asset_id",
  "asset_type",
  "asset_source_uri",
  "asset_storage_bucket",
  "asset_storage_path",
  "asset_alt_text",
  "asset_generated_description",
] as const;

const REQUIRED_POC_COLUMNS = [
  "full_address",
  "suburb",
  "postcode",
  "sale_price",
  "sale_date",
  "listing_type",
  "property_type",
  "bedrooms",
  "bathrooms",
  "parking",
];

const EXPORT_PRESETS: ExportPreset[] = [
  {
    id: "tim-poc-minimum",
    label: "Tim POC Minimum",
    description: "Minimum valuation-ready columns from sold sample requirements.",
    columns: REQUIRED_POC_COLUMNS,
    defaultChunkSize: 10000,
  },
  {
    id: "provenance-full",
    label: "Full Provenance",
    description: "All provenance, snapshot, and asset fields.",
    columns: [...KNOWN_EXPORT_COLUMNS],
    defaultChunkSize: 10000,
  },
];

const POSTCODE_RE = /^\d{4}$/;

function parsePostcodesInput(raw: string): { valid: string[]; invalid: string[] } {
  if (!raw.trim()) return { valid: [], invalid: [] };
  const tokens = raw
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const token of tokens) {
    if (POSTCODE_RE.test(token)) valid.push(token);
    else invalid.push(token);
  }
  return { valid, invalid };
}

const ExportsPage = () => {
  const { toast } = useToast();
  const { canManageJobs } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [adapterFilter, setAdapterFilter] = useState<string>("all");
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adapters, setAdapters] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportRuns, setExportRuns] = useState<SnapshotExportRun[]>([]);
  const [exportRuntime, setExportRuntime] = useState<ExportRuntimeSettings | null>(null);
  const [exportFilters, setExportFilters] = useState<SnapshotExportFilters>({
    limit: 10000,
  });
  const [postcodesInput, setPostcodesInput] = useState("");
  const [exportName, setExportName] = useState("");
  const [chunkSizeRows, setChunkSizeRows] = useState(10000);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedExportRun, setSelectedExportRun] = useState<SnapshotExportRun | null>(null);
  const [selectedPresetId, setSelectedPresetId] = useState<string>("tim-poc-minimum");

  const postcodeParse = useMemo(
    () => parsePostcodesInput(postcodesInput),
    [postcodesInput],
  );

  const loadFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // List all files in scraped-data bucket
      const { data: folders, error: folderError } = await supabase.storage
        .from('scraped-data')
        .list('', { limit: 100 });

      if (folderError) {
        console.error('Error listing folders:', folderError);
        throw folderError;
      }

      const allFiles: StorageFile[] = [];
      const adapterSet = new Set<string>();

      // List files in each adapter folder
      for (const item of folders || []) {
        // Folders have id=null and no metadata, files have id and metadata
        const isFolder = item.id === null && !item.metadata;
        
        if (isFolder) {
          // It's a folder - list files inside
          const { data: filesInFolder } = await supabase.storage
            .from('scraped-data')
            .list(item.name, { limit: 100 });

          for (const file of filesInFolder || []) {
            const isNestedFolder = file.id === null && !file.metadata;
            if (isNestedFolder) continue;
            adapterSet.add(item.name);
            allFiles.push({
              id: `${item.name}/${file.name}`,
              name: file.name,
              adapter: item.name,
              created_at: file.created_at || new Date().toISOString(),
              size: file.metadata?.size || 0,
              metadata: null,
            });
          }
        } else {
          // Root level file
          allFiles.push({
            id: item.name,
            name: item.name,
            adapter: 'unknown',
            created_at: item.created_at || new Date().toISOString(),
            size: item.metadata?.size || 0,
            metadata: null,
          });
        }
      }

      // Sort by date descending
      allFiles.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      setFiles(allFiles);
      setAdapters(Array.from(adapterSet));
    } catch (error) {
      console.error('Failed to load files:', error);
      toast({
        title: "Error",
        description: "Failed to load storage files",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const loadRuntimeConfig = useCallback(async () => {
    const runtimeResolution = await fetchExportRuntimeResolution();
    const effective = runtimeResolution?.effective ?? (await fetchExportRuntimeSettings());
    setExportRuntime(effective);
    setChunkSizeRows(effective.chunkSizeRows);
    setSelectedColumns(effective.defaultColumns);
    setExportFilters((prev) => ({
      ...prev,
      limit: prev.limit || effective.maxRows,
    }));
  }, []);

  const loadExports = useCallback(async () => {
    const runs = await listSnapshotExports();
    setExportRuns(runs);
  }, []);

  useEffect(() => {
    void loadFiles();
    void loadExports();
    void loadRuntimeConfig();
  }, [loadExports, loadFiles, loadRuntimeConfig]);

  const handleGenerateProvenanceExport = async (mode: "everything" | "filtered") => {
    try {
      setIsExporting(true);
      const isFiltered = mode === "filtered";
      const normalizedFilters: SnapshotExportFilters = isFiltered
        ? {
            postcodes: postcodeParse.valid.length > 0 ? postcodeParse.valid : undefined,
            state: exportFilters.state?.trim() || undefined,
            adapterSlug: exportFilters.adapterSlug || undefined,
            provider: exportFilters.provider || undefined,
            fromDate: exportFilters.fromDate || undefined,
            toDate: exportFilters.toDate || undefined,
            limit: exportFilters.limit || exportRuntime?.maxRows || 1000000,
          }
        : {};
      const result = await runSnapshotProvenanceExport({
        format: "csv",
        filters: normalizedFilters,
        exportOptions: {
          exportName: exportName.trim() || undefined,
          chunkSizeRows,
          selectedColumns: selectedColumns.length > 0 ? selectedColumns : exportRuntime?.defaultColumns || [],
        },
      });

      if (!result.success) {
        throw new Error(result.error || "Export failed");
      }

      toast({
        title: "Export generated",
        description: `Created ${result.rowCount || 0} rows in ${result.chunkCount || 1} chunk(s)`,
      });
      await Promise.all([loadFiles(), loadExports()]);
    } catch (error) {
      console.error("Failed to generate provenance export:", error);
      toast({
        title: "Export failed",
        description: "Could not generate provenance export",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const clearExportFilters = () => {
    setExportFilters({ limit: exportRuntime?.maxRows || 1000000 });
    setPostcodesInput("");
    setExportName("");
    setChunkSizeRows(exportRuntime?.chunkSizeRows || 10000);
    setSelectedColumns(exportRuntime?.defaultColumns || []);
  };

  const handleApplyPreset = (presetId: string) => {
    const preset = EXPORT_PRESETS.find((item) => item.id === presetId);
    if (!preset) return;
    setSelectedPresetId(presetId);
    setSelectedColumns(preset.columns);
    if (preset.defaultChunkSize) {
      setChunkSizeRows(preset.defaultChunkSize);
    }
    toast({
      title: "Preset applied",
      description: preset.description,
    });
  };

  const toggleSelectedColumn = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column) ? prev.filter((item) => item !== column) : [...prev, column]
    );
  };

  const handleDownloadExport = async (run: SnapshotExportRun) => {
    const manifestPath = run.manifest_path || run.file_path;
    const targetPath = manifestPath || run.file_path;
    if (!targetPath) return;
    const bucket = run.file_bucket || "scraped-data";
    try {
      const downloadBlob = (blob: Blob, fileName: string) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      };

      if (manifestPath?.endsWith(".json")) {
        const { data: manifestBlob, error: manifestError } = await supabase.storage
          .from(bucket)
          .download(manifestPath);
        if (manifestError) throw manifestError;
        const manifestText = await manifestBlob.text();
        const manifest = JSON.parse(manifestText) as {
          chunks?: Array<{ path: string; rowCount: number }>;
        };
        const chunks = manifest.chunks || [];
        if (chunks.length > 0) {
          for (const chunk of chunks) {
            const { data: chunkBlob, error: chunkError } = await supabase.storage
              .from(bucket)
              .download(chunk.path);
            if (chunkError) throw chunkError;
            const fileName = chunk.path.split("/").pop() || "export-chunk.csv";
            downloadBlob(chunkBlob, fileName);
          }
          toast({
            title: "Chunked export downloaded",
            description: `${chunks.length} file(s) downloaded`,
          });
          return;
        }
      }

      const { data, error } = await supabase.storage.from(bucket).download(targetPath);
      if (error) throw error;
      const fileName = targetPath.split("/").pop() || `export-${run.id}.csv`;
      downloadBlob(data, fileName);
    } catch (error) {
      console.error("Failed to download export:", error);
      toast({
        title: "Download failed",
        description: "Could not download the export file",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (file: StorageFile) => {
    try {
      const { data, error } = await supabase.storage
        .from('scraped-data')
        .download(file.id);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download:', error);
      toast({
        title: "Error",
        description: "Failed to download file",
        variant: "destructive"
      });
    }
  };

  const removeFilesInBatches = async (fileIds: string[]) => {
    const batchSize = 100;
    for (let i = 0; i < fileIds.length; i += batchSize) {
      const batch = fileIds.slice(i, i + batchSize);
      const { error } = await supabase.storage
        .from('scraped-data')
        .remove(batch);

      if (error) {
        throw error;
      }
    }
  };

  const handleDelete = async (fileIds: string[]) => {
    try {
      await removeFilesInBatches(fileIds);

      toast({
        title: "Deleted",
        description: `${fileIds.length} file(s) deleted`
      });
      
      setSelectedFiles([]);
      loadFiles();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast({
        title: "Error",
        description: "Failed to delete files",
        variant: "destructive"
      });
    }
  };

  const openDeleteDialog = (fileIds: string[]) => {
    setPendingDeleteIds(fileIds);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteIds.length === 0) return;
    await handleDelete(pendingDeleteIds);
    setPendingDeleteIds([]);
    setDeleteDialogOpen(false);
  };

  const exportArtifactPaths = new Set<string>();
  for (const run of exportRuns) {
    const mainPath = run.file_path;
    const manifestPath = run.manifest_path || run.file_path;
    if (mainPath) exportArtifactPaths.add(mainPath);
    if (manifestPath) exportArtifactPaths.add(manifestPath);
    if (manifestPath?.endsWith(".json")) {
      const basePath = manifestPath
        .replace("/metadata.json", "")
        .replace("-manifest.json", "");
      for (const file of files) {
        if (file.id.startsWith(`${basePath}/part-`) || file.id.startsWith(`${basePath}-part-`)) {
          exportArtifactPaths.add(file.id);
        }
      }
    }
  }

  const filteredFiles = files.filter((file) => {
    if (exportArtifactPaths.has(file.id)) return false;
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAdapter = adapterFilter === "all" || file.adapter === adapterFilter;
    return matchesSearch && matchesAdapter;
  });
  const filteredExportRuns = exportRuns.filter((run) => {
    const needle = searchQuery.trim().toLowerCase();
    if (!needle) return true;
    const byName = run.export_name?.toLowerCase().includes(needle);
    const byPath = run.file_path?.toLowerCase().includes(needle);
    const byManifestPath = run.manifest_path?.toLowerCase().includes(needle);
    const byId = run.id.toLowerCase().includes(needle);
    return Boolean(byName || byPath || byManifestPath || byId);
  });
  const availableColumns = Array.from(
    new Set([...KNOWN_EXPORT_COLUMNS, ...(exportRuntime?.defaultColumns || []), ...selectedColumns])
  );
  const missingPocColumns = REQUIRED_POC_COLUMNS.filter((column) => !availableColumns.includes(column));

  const totalSize =
    filteredFiles.reduce((sum, file) => sum + file.size, 0) +
    filteredExportRuns.reduce((sum, run) => sum + (run.file_size_bytes || 0), 0);

  const toggleFileSelection = (id: string) => {
    setSelectedFiles((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const toggleAllFiles = () => {
    if (selectedFiles.length === filteredFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredFiles.map((f) => f.id));
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Exports</h1>
          <p className="text-muted-foreground">
            Configure exports, runtime defaults, and downloadable output files
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            onClick={() => handleGenerateProvenanceExport("everything")}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export Everything CSV
          </Button>
          <HelpTooltip
            title="Export Everything"
            summary="No segment filters. Exports all matching rows."
            details="Uses chunk files to avoid timeout and memory issues."
          />
          <Button
            variant="outline"
            onClick={() => handleGenerateProvenanceExport("filtered")}
            disabled={isExporting}
          >
            Export Filtered CSV
          </Button>
          <HelpTooltip
            title="Export Segment"
            summary="Only rows matching your filter values."
            details="Use this for focused extracts by state, suburb, adapter, provider, or date."
          />
          <span className="text-sm text-muted-foreground">
            {filteredExportRuns.length} exports
            {filteredFiles.length > 0 && ` • ${filteredFiles.length} files`}
            {" • "}{formatFileSize(totalSize)} total
          </span>
          <Button variant="outline" size="icon" onClick={loadFiles}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="card-elevated p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1">
              <h2 className="text-sm font-semibold">Export Segment & Output</h2>
              <HelpTooltip
                title="Segments"
                summary="Leave blank for broad scope."
                details="Set filters, row limit, chunk size, and columns before exporting."
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Choose rows, columns, and chunking. Minimal inputs, maximum control.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearExportFilters}
            >
              Clear
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => handleGenerateProvenanceExport("filtered")}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export Segment CSV
            </Button>
          </div>
        </div>

        <div className="rounded-md border p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="export-preset" className="text-xs">Export Preset</Label>
            <HelpTooltip
              title="Presets"
              summary="Quickly apply saved column sets."
              details="Use Tim POC Minimum for tonight's valuation-ready extract baseline."
            />
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <Select value={selectedPresetId} onValueChange={setSelectedPresetId}>
              <SelectTrigger id="export-preset" className="md:w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPORT_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleApplyPreset(selectedPresetId)}
            >
              Apply Preset
            </Button>
            <span className="text-xs text-muted-foreground">
              {EXPORT_PRESETS.find((preset) => preset.id === selectedPresetId)?.description}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-name" className="text-xs">Export Name</Label>
              <HelpTooltip title="Name" summary="Optional run label." details="Appears in export history and metadata." />
            </div>
            <Input
              id="export-name"
              placeholder="e.g. nightly-boss-extract"
              value={exportName}
              onChange={(event) => setExportName(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-chunk-size" className="text-xs">Chunk Size (rows)</Label>
              <HelpTooltip
                title="Chunk Size"
                summary="Rows per output file."
                details="Use 10,000 for stable large exports."
              />
            </div>
            <Input
              id="export-chunk-size"
              type="number"
              min={1000}
              max={100000}
              value={chunkSizeRows}
              onChange={(event) => setChunkSizeRows(parseInt(event.target.value || "10000", 10))}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-state-filter" className="text-xs">State</Label>
              <HelpTooltip title="State Filter" summary="Restrict export to one state." />
            </div>
            <Select
              value={exportFilters.state || "any"}
              onValueChange={(value) =>
                setExportFilters((prev) => ({ ...prev, state: value === "any" ? undefined : value }))
              }
            >
              <SelectTrigger id="export-state-filter">
                <SelectValue placeholder="Any state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="ACT">ACT</SelectItem>
                <SelectItem value="NSW">NSW</SelectItem>
                <SelectItem value="NT">NT</SelectItem>
                <SelectItem value="QLD">QLD</SelectItem>
                <SelectItem value="SA">SA</SelectItem>
                <SelectItem value="TAS">TAS</SelectItem>
                <SelectItem value="VIC">VIC</SelectItem>
                <SelectItem value="WA">WA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-postcodes-filter" className="text-xs">Postcodes</Label>
              <HelpTooltip
                title="Postcode Filter"
                summary="Comma-separated 4-digit postcodes."
                details="e.g. 3204, 3186, 3000. Leave blank to include all."
              />
            </div>
            <Input
              id="export-postcodes-filter"
              placeholder="e.g. 3204, 3186, 3000"
              value={postcodesInput}
              onChange={(event) => setPostcodesInput(event.target.value)}
            />
            {postcodeParse.invalid.length > 0 ? (
              <p className="text-[10px] text-amber-600">
                Ignored (not 4-digit): {postcodeParse.invalid.join(", ")}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-adapter-filter" className="text-xs">Adapter</Label>
              <HelpTooltip title="Adapter Filter" summary="Source website adapter slug." />
            </div>
            <Select
              value={exportFilters.adapterSlug || "any"}
              onValueChange={(value) =>
                setExportFilters((prev) => ({ ...prev, adapterSlug: value === "any" ? undefined : value }))
              }
            >
              <SelectTrigger id="export-adapter-filter">
                <SelectValue placeholder="Any adapter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {adapters.map((adapter) => (
                  <SelectItem key={adapter} value={adapter}>
                    {adapter}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-provider-filter" className="text-xs">Provider</Label>
              <HelpTooltip title="Provider Filter" summary="Scraping provider used for the snapshot." />
            </div>
            <Select
              value={exportFilters.provider || "any"}
              onValueChange={(value) =>
                setExportFilters((prev) => ({ ...prev, provider: value === "any" ? undefined : value }))
              }
            >
              <SelectTrigger id="export-provider-filter">
                <SelectValue placeholder="Any provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="firecrawl_scrape">firecrawl_scrape</SelectItem>
                <SelectItem value="firecrawl_agent">firecrawl_agent</SelectItem>
                <SelectItem value="zyte">zyte</SelectItem>
                <SelectItem value="direct">direct</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-from-date" className="text-xs">From Date</Label>
              <HelpTooltip title="Start Date" summary="Include snapshots on or after this date." />
            </div>
            <Input
              id="export-from-date"
              type="date"
              value={exportFilters.fromDate || ""}
              onChange={(event) =>
                setExportFilters((prev) => ({ ...prev, fromDate: event.target.value || undefined }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-to-date" className="text-xs">To Date</Label>
              <HelpTooltip title="End Date" summary="Include snapshots on or before this date." />
            </div>
            <Input
              id="export-to-date"
              type="date"
              value={exportFilters.toDate || ""}
              onChange={(event) =>
                setExportFilters((prev) => ({ ...prev, toDate: event.target.value || undefined }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="export-row-limit" className="text-xs">Row Limit</Label>
              <HelpTooltip title="Row Limit" summary="Hard cap for this export run." />
            </div>
            <Input
              id="export-row-limit"
              type="number"
              min={1}
              max={exportRuntime?.maxRows || 1000000}
              value={exportFilters.limit || exportRuntime?.maxRows || 1000000}
              onChange={(event) =>
                setExportFilters((prev) => ({
                  ...prev,
                  limit: event.target.value ? parseInt(event.target.value, 10) : exportRuntime?.maxRows || 1000000,
                }))
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <p className="text-xs">Columns</p>
            <HelpTooltip
              title="Column Selection"
              summary="Only selected columns are exported."
              details="Use fewer columns for smaller files and faster downstream processing."
            />
            <Badge variant="outline" className="text-xs">{selectedColumns.length} selected</Badge>
          </div>
          <ScrollArea className="h-28 rounded border p-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {availableColumns.map((column) => (
                <div key={column} className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={selectedColumns.includes(column)}
                    onCheckedChange={() => toggleSelectedColumn(column)}
                  />
                  <span className="font-mono">{column}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="text-xs text-muted-foreground">
            POC minimum columns: {REQUIRED_POC_COLUMNS.join(", ")}
          </div>
          {missingPocColumns.length > 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Missing from current export schema: {missingPocColumns.join(", ")}.
              </AlertDescription>
            </Alert>
          ) : null}
        </div>
      </div>

      <ExportPreviewTable
        filters={{
          postcodes: postcodeParse.valid.length > 0 ? postcodeParse.valid : undefined,
          state: exportFilters.state,
          adapterSlug: exportFilters.adapterSlug,
          fromDate: exportFilters.fromDate,
          toDate: exportFilters.toDate,
        }}
      />

      <div className="card-elevated">
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search exports and files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={adapterFilter} onValueChange={setAdapterFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {adapters.map((adapter) => (
                  <SelectItem key={adapter} value={adapter}>
                    {adapter}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canManageJobs && (
              <div className="flex items-center gap-2">
                {selectedFiles.length > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => openDeleteDialog(selectedFiles)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete ({selectedFiles.length})
                  </Button>
                )}
                {filteredFiles.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDeleteDialog(filteredFiles.map((file) => file.id))}
                  >
                    Delete All
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredFiles.length === 0 && filteredExportRuns.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No exports or files found</p>
            <p className="text-sm">Run an export or scrape job to generate output artifacts</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={selectedFiles.length === filteredFiles.length && filteredFiles.length > 0}
                    onCheckedChange={toggleAllFiles}
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Rows</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Size</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredExportRuns.map((run) => (
                <TableRow key={run.id}>
                  <TableCell />
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <FileJson className="w-4 h-4 text-accent" />
                        <span className="font-medium">{run.export_name || `Export ${run.id.slice(0, 8)}`}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">
                          {run.format.toUpperCase()}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={statusBadgeClass[run.status] || "bg-muted text-muted-foreground"}
                        >
                          {run.status}
                        </Badge>
                        {run.chunk_count ? (
                          <Badge variant="outline" className="text-[10px]">
                            {run.chunk_count} chunks
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-normal">
                      Export run
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {(run.manifest_path || run.file_path)
                      ? (run.manifest_path || run.file_path)?.split("/").slice(0, -1).join("/") || "root"
                      : "pending"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{run.row_count.toLocaleString()}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(run.created_at, { includeTime: true })}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {run.file_size_bytes ? formatFileSize(run.file_size_bytes) : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {run.file_path ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownloadExport(run)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setSelectedExportRun(run)}
                      >
                        <Info className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredFiles.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedFiles.includes(file.id)}
                      onCheckedChange={() => toggleFileSelection(file.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileJson className="w-4 h-4 text-accent" />
                      <span className="font-mono text-sm">{file.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-normal">
                      File
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-normal">
                      {file.adapter}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">—</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(file.created_at, { includeTime: true })}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatFileSize(file.size)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handleDownload(file)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!selectedExportRun} onOpenChange={() => setSelectedExportRun(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Export Details</DialogTitle>
            <DialogDescription>
              Summary metrics, selected columns, and distribution metadata for this export.
            </DialogDescription>
          </DialogHeader>
          {selectedExportRun ? (
            <div className="space-y-3 text-sm">
              {(() => {
                const metadata = (selectedExportRun.metadata || {}) as Record<string, unknown>;
                const filters = (metadata.filters || {}) as Record<string, unknown>;
                const dateRange = (metadata.dateRange || {}) as Record<string, string | null>;
                const rawTopStates = metadata.topStates as
                  | Array<{ label?: string; value?: number; pct?: number; percent?: number }>
                  | { items?: Array<{ label?: string; count?: number; percent?: number }> }
                  | undefined;
                const rawTopSuburbs = metadata.topSuburbs as
                  | Array<{ label?: string; value?: number; pct?: number; percent?: number }>
                  | { items?: Array<{ label?: string; count?: number; percent?: number }> }
                  | undefined;
                const topStates = Array.isArray(rawTopStates)
                  ? rawTopStates
                  : Array.isArray(rawTopStates?.items)
                    ? rawTopStates.items
                    : [];
                const topSuburbs = Array.isArray(rawTopSuburbs)
                  ? rawTopSuburbs
                  : Array.isArray(rawTopSuburbs?.items)
                    ? rawTopSuburbs.items
                    : [];

                return (
                  <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p>{selectedExportRun.export_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p>{formatDate(selectedExportRun.created_at, { includeTime: true })}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Rows</p>
                  <p>{selectedExportRun.row_count.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Chunking</p>
                  <p>
                    {selectedExportRun.chunk_count || 1} file(s) • {selectedExportRun.chunk_size_rows || 0} rows/chunk
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">File</p>
                  <p className="font-mono text-xs break-all">{selectedExportRun.file_path || "—"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Manifest</p>
                  <p className="font-mono text-xs break-all">{selectedExportRun.manifest_path || "—"}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Selected Columns</p>
                <ScrollArea className="h-28 rounded border p-2">
                  <div className="flex flex-wrap gap-1.5">
                    {(selectedExportRun.selected_columns || []).map((column) => (
                      <Badge key={column} variant="outline" className="text-[10px] font-mono">
                        {column}
                      </Badge>
                    ))}
                    {!selectedExportRun.selected_columns?.length ? (
                      <span className="text-xs text-muted-foreground">No column metadata stored.</span>
                    ) : null}
                  </div>
                </ScrollArea>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Filters Used</p>
                <div className="rounded border p-2 text-xs">
                  {Object.keys(filters).length === 0 ? (
                    <span className="text-muted-foreground">No filters (export everything).</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(filters).map(([key, value]) => (
                        <Badge key={key} variant="outline" className="text-[10px]">
                          {key}: {String(value)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Date Range</p>
                  <div className="rounded border p-2 text-xs space-y-1">
                    <p>Min: {dateRange.min || "—"}</p>
                    <p>Q1: {dateRange.q1 || "—"}</p>
                    <p>Q3: {dateRange.q3 || "—"}</p>
                    <p>Max: {dateRange.max || "—"}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Coverage (Top 80%)</p>
                  <div className="rounded border p-2 text-xs space-y-2">
                    <div>
                      <p className="font-medium text-[11px]">States</p>
                      {topStates.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {topStates.map((item) => (
                            <Badge key={`state-${item.label}`} variant="outline" className="text-[10px]">
{item.label}: {('pct' in item && item.pct) ?? ('percent' in item && item.percent) ?? 0}%
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No state breakdown.</p>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[11px]">Suburbs</p>
                      {topSuburbs.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {topSuburbs.slice(0, 8).map((item) => (
                            <Badge key={`suburb-${item.label}`} variant="outline" className="text-[10px]">
{item.label}: {('pct' in item && item.pct) ?? ('percent' in item && item.percent) ?? 0}%
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No suburb breakdown.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Run Metadata</p>
                <ScrollArea className="h-40 rounded border p-2">
                  <pre className="text-[11px] whitespace-pre-wrap break-words">
                    {JSON.stringify(selectedExportRun.metadata || {}, null, 2)}
                  </pre>
                </ScrollArea>
              </div>
                  </>
                );
              })()}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setPendingDeleteIds([]);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete files?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteIds.length === 1
                ? "This will permanently delete 1 file."
                : `This will permanently delete ${pendingDeleteIds.length} files.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExportsPage;
